import { useCallback, useEffect, useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  NativeSelect,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { api, errorMessage, isSubscriberDataRestricted } from '../api/client';
import type {
  BrandSummary,
  EmailList,
  ExportLink,
  InteractionSummary,
  Subscriber,
  SubscriberFilter,
  SubscriberPage,
  SubscriptionStatus,
  SubscriptionSummary,
} from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { subscriptionStatusLabel, whenLabel } from '../format';

/**
 * Everybody who has given a publisher their email address, and the three ways to get at them.
 *
 * The counts sit above the list under the same filters, because a publisher looking at a filtered
 * list wants the number for that filter rather than for everything. They are derived by reading
 * the records and stop at 20,000, so past that they are a floor: this screen says "at least" and
 * never dresses a floor up as a total, because a floor presented as a total is a number somebody
 * would plan a mailing against.
 *
 * A publisher can decide that only its owners see the addresses themselves, and then this screen
 * is refused the list and the export while keeping the counts. That case is drawn as a sentence
 * saying so, never as an empty table: an empty table reads as "nobody has signed up", which is a
 * different fact and almost always a false one. The counts stay because they are not personal
 * data, and an editor who cannot tell whether the newsletter is working cannot do their job.
 */
export function Subscribers() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';

  const [draft, setDraft] = useState<FilterDraft>(EMPTY_FILTER);
  const [rows, setRows] = useState<readonly Subscriber[] | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [brands, setBrands] = useState<readonly BrandSummary[]>([]);
  const [interactions, setInteractions] = useState<readonly InteractionSummary[]>([]);
  // The lists on whichever brand is currently chosen. A list belongs to one brand, so there is
  // nothing to offer here until a brand has been picked.
  const [brandLists, setBrandLists] = useState<readonly EmailList[]>([]);

  /**
   * Refused the addresses, which is not an error and is not an empty list.
   *
   * It is learnt from the server rather than worked out here from a role and a setting: the server
   * is where the rule lives, so a screen that decided for itself would be a second copy of it,
   * free to disagree.
   */
  const [restricted, setRestricted] = useState(false);

  const [download, setDownload] = useState<ExportLink | null>(null);
  const [exportProblem, setExportProblem] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const filter = asFilter(draft);
  const filtered = isFiltered(draft);

  const load = useCallback(
    (current: FilterDraft) => {
      if (orgId === null) return;
      const applied = asFilter(current);
      setProblem(null);
      setRows(null);
      setSummary(null);
      setDownload(null);
      setRestricted(false);
      Promise.all([
        api.subscriptionSummary(orgId, applied),
        // A refusal is an answer rather than a failure, so it is caught here and carried through
        // as one. Anything else is thrown on and shown as the problem it is.
        api.subscribers(orgId, applied).catch((error: unknown): SubscriberPage | typeof REFUSED => {
          if (isSubscriberDataRestricted(error)) return REFUSED;
          throw error;
        }),
      ])
        .then(([counts, page]) => {
          setSummary(counts);
          if (page === REFUSED) {
            setRestricted(true);
            return;
          }
          setRows(page.subscribers);
          setCursor(page.cursor);
        })
        .catch((error: unknown) => setProblem(errorMessage(error)));
    },
    [orgId],
  );

  useEffect(() => load(draft), [load, draft]);

  /**
   * The two lists behind the filters, loaded once and allowed to fail quietly. A publisher whose
   * brands did not come back still gets their subscribers, with two fewer ways to narrow them.
   */
  useEffect(() => {
    if (orgId === null) return;
    api
      .brands(orgId)
      .then((body) => setBrands(body.brands))
      .catch(() => setBrands([]));
    api
      .interactions(orgId)
      .then((body) => setInteractions(body.interactions))
      .catch(() => setInteractions([]));
  }, [orgId]);

  /**
   * The lists on the brand the filter is narrowed to, refetched whenever that brand changes.
   *
   * Allowed to fail quietly, like the two lists above: a publisher whose lists did not come back
   * still gets their subscribers, with one fewer way to narrow them.
   */
  useEffect(() => {
    if (orgId === null || draft.brand_id === '') {
      setBrandLists([]);
      return;
    }
    let live = true;
    api
      .lists(orgId, draft.brand_id)
      .then((body) => {
        if (live) setBrandLists(body.lists);
      })
      .catch(() => {
        if (live) setBrandLists([]);
      });
    return () => {
      live = false;
    };
  }, [orgId, draft.brand_id]);

  const listName = brandLists.find((list) => list.list_id === draft.list_id)?.name;

  function showMore() {
    if (orgId === null || cursor === undefined) return;
    setLoadingMore(true);
    api
      .subscribers(orgId, { ...filter, cursor })
      .then((page) => {
        setRows((existing) => [...(existing ?? []), ...page.subscribers]);
        setCursor(page.cursor);
      })
      .catch((error: unknown) => setProblem(errorMessage(error)))
      .finally(() => setLoadingMore(false));
  }

  async function requestExport() {
    if (orgId === null) return;
    setExportProblem(null);
    setDownload(null);
    setExporting(true);
    try {
      setDownload(await api.exportSubscribers(orgId, filter));
    } catch (error) {
      setExportProblem(errorMessage(error));
    } finally {
      setExporting(false);
    }
  }

  function titleOf(interactionId: string): string {
    return (
      interactions.find((item) => item.interaction_id === interactionId)?.title ?? interactionId
    );
  }

  function nameOf(brandId: string): string {
    return brands.find((item) => item.brand_id === brandId)?.name ?? brandId;
  }

  return (
    <>
      <Group justify="space-between" align="center" gap="md" mb="md" wrap="wrap">
        <Title order={1} size="h2">
          Subscribers
        </Title>
        <Anchor component={Link} to="/settings/developers" size="sm">
          API keys
        </Anchor>
      </Group>
      <Text size="sm" c="dimmed" mb="lg">
        {restricted
          ? `How many readers have given ${orgName} their email address. The addresses themselves are kept for owners on this publisher.`
          : `Every reader who has given ${orgName} their email address, with the wording they agreed to kept beside them. Take them away as a CSV, have them posted to your own system as they arrive, or pull them with an API key.`}
      </Text>

      <Filters
        draft={draft}
        onChange={setDraft}
        brands={brands}
        interactions={interactions}
        lists={brandLists}
        filtered={filtered}
      />

      {problem !== null && <ErrorNote message={problem} onRetry={() => load(draft)} />}

      {summary !== null && <Summary summary={summary} />}

      {restricted ? (
        <Restricted orgName={orgName} />
      ) : (
        <ExportPanel
          onExport={() => void requestExport()}
          exporting={exporting}
          download={download}
          problem={exportProblem}
          filtered={filtered}
          listName={listName}
        />
      )}

      {problem === null && rows === null && !restricted && (
        <Loading label="Loading your subscribers" />
      )}

      {rows !== null && rows.length === 0 && (
        <EmptyState filtered={filtered} onClear={() => setDraft(EMPTY_FILTER)} />
      )}

      {rows !== null && rows.length > 0 && (
        <>
          <Table.ScrollContainer minWidth={720} mt="lg">
            <Table verticalSpacing="sm" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email address</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Brand</Table.Th>
                  <Table.Th>Signed up on</Table.Th>
                  <Table.Th>When</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.subscription_id}>
                    <Table.Td>
                      <Text size="sm">{row.email}</Text>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        Agreed to: {row.consent.text}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <SubscriptionStatusBadge status={row.status} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{nameOf(row.brand_id)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{titleOf(row.interaction_id)}</Text>
                      {row.placement_ref !== undefined && (
                        <Text size="xs" c="dimmed">
                          {row.placement_ref}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{whenLabel(row.created_at)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          {cursor !== undefined && (
            <Group mt="md">
              <Button variant="default" disabled={loadingMore} onClick={showMore}>
                {loadingMore ? 'Loading...' : 'Show more'}
              </Button>
            </Group>
          )}
        </>
      )}
    </>
  );
}

/**
 * The answer the list gives somebody who may not see it.
 *
 * A value of its own rather than null or an empty page, so that "you are not allowed these" cannot
 * be confused with "there are none" anywhere between the request and the screen.
 */
const REFUSED: unique symbol = Symbol('subscriber addresses withheld');

/**
 * What a member is told when the publisher keeps addresses for owners.
 *
 * It says the counts above are whole, because the obvious reading of a screen with numbers and no
 * rows is that something is broken or that the list is short. And it says who can change it, since
 * the person reading this cannot and the next thing they will do is ask somebody who can.
 */
function Restricted({ orgName }: { orgName: string }) {
  return (
    <Card withBorder radius="lg" p="lg" component="section" mb="md">
      <Title order={2} size="h3">
        These addresses are kept for owners
      </Title>
      <Text mt="xs">
        {orgName} lets only its owners see and export subscriber email addresses. The counts above
        are every signup that matches the filters, so nothing is missing from them: what is held
        back is who those readers are.
      </Text>
      <Text size="sm" c="dimmed" mt="xs">
        An owner can change this under Settings, then Security.
      </Text>
    </Card>
  );
}

/** The filter state as it is typed, where an empty string means "not filtering on this". */
interface FilterDraft {
  readonly brand_id: string;
  readonly interaction_id: string;
  readonly status: string;
  readonly from: string;
  readonly to: string;
  readonly list_id: string;
}

const EMPTY_FILTER: FilterDraft = {
  brand_id: '',
  interaction_id: '',
  status: '',
  from: '',
  to: '',
  list_id: '',
};

const STATUSES: readonly SubscriptionStatus[] = ['subscribed', 'pending', 'unsubscribed'];

function isStatus(value: string): value is SubscriptionStatus {
  return (STATUSES as readonly string[]).includes(value);
}

/** What is actually sent. Anything left blank is left out rather than sent as an empty filter. */
function asFilter(draft: FilterDraft): SubscriberFilter {
  return {
    ...(draft.brand_id === '' ? {} : { brand_id: draft.brand_id }),
    ...(draft.interaction_id === '' ? {} : { interaction_id: draft.interaction_id }),
    ...(isStatus(draft.status) ? { status: draft.status } : {}),
    ...(draft.from === '' ? {} : { from: draft.from }),
    ...(draft.to === '' ? {} : { to: draft.to }),
    ...(draft.list_id === '' ? {} : { list_id: draft.list_id }),
  };
}

function isFiltered(draft: FilterDraft): boolean {
  return Object.values(draft).some((value) => value !== '');
}

function Filters({
  draft,
  onChange,
  brands,
  interactions,
  lists,
  filtered,
}: {
  draft: FilterDraft;
  onChange: (draft: FilterDraft) => void;
  brands: readonly BrandSummary[];
  interactions: readonly InteractionSummary[];
  lists: readonly EmailList[];
  filtered: boolean;
}) {
  return (
    <Card withBorder radius="lg" p="md" component="section" mb="md">
      <Group gap="md" align="flex-end" wrap="wrap">
        <NativeSelect
          id="subscribers-brand"
          label="Brand"
          value={draft.brand_id}
          // Changing the brand clears the list, because a list belongs to one brand and a list id
          // from the old one would silently filter against a masthead nobody chose any more.
          onChange={(event) => onChange({ ...draft, brand_id: event.target.value, list_id: '' })}
          data={[
            { value: '', label: 'All brands' },
            ...brands.map((brand) => ({ value: brand.brand_id, label: brand.name })),
          ]}
          style={{ minWidth: '12rem' }}
        />
        <NativeSelect
          id="subscribers-list"
          label="List"
          value={draft.list_id}
          onChange={(event) => onChange({ ...draft, list_id: event.target.value })}
          disabled={draft.brand_id === ''}
          data={[
            { value: '', label: draft.brand_id === '' ? 'Choose a brand first' : 'All lists' },
            ...lists.map((list) => ({
              value: list.list_id,
              label: list.is_default
                ? `${list.name} (default)`
                : list.archived
                  ? `${list.name} (archived)`
                  : list.name,
            })),
          ]}
          style={{ minWidth: '14rem' }}
        />
        <NativeSelect
          id="subscribers-interaction"
          label="Signed up on"
          value={draft.interaction_id}
          onChange={(event) => onChange({ ...draft, interaction_id: event.target.value })}
          data={[
            { value: '', label: 'Anything' },
            ...interactions.map((item) => ({
              value: item.interaction_id,
              label: item.title,
            })),
          ]}
          style={{ minWidth: '14rem' }}
        />
        <NativeSelect
          id="subscribers-status"
          label="Status"
          value={draft.status}
          onChange={(event) => onChange({ ...draft, status: event.target.value })}
          data={[
            { value: '', label: 'Any status' },
            ...STATUSES.map((status) => ({
              value: status,
              label: subscriptionStatusLabel(status),
            })),
          ]}
          style={{ minWidth: '11rem' }}
        />
        <TextInput
          id="subscribers-from"
          type="date"
          label="From"
          value={draft.from}
          onChange={(event) => onChange({ ...draft, from: event.currentTarget.value })}
        />
        <TextInput
          id="subscribers-to"
          type="date"
          label="To"
          description="Includes the whole of that day."
          value={draft.to}
          onChange={(event) => onChange({ ...draft, to: event.currentTarget.value })}
        />
        {filtered && (
          <Button variant="subtle" color="gray" onClick={() => onChange(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
      </Group>
    </Card>
  );
}

/**
 * The counts, and the sentence that has to appear with them once counting has stopped early.
 *
 * Every number on this card is a floor when `truncated` is true, so every one of them is written
 * as "at least". Saying it once at the bottom and printing bare numbers above would be worse than
 * not showing the card at all.
 */
function Summary({ summary }: { summary: SubscriptionSummary }) {
  const at = summary.truncated ? 'At least ' : '';

  return (
    <Card withBorder radius="lg" p="lg" component="section" mb="md">
      <Group gap="xl" wrap="wrap" align="flex-start">
        <Figure
          label={summary.truncated ? 'At least, in total' : 'In total'}
          value={summary.total}
        />
        <Figure label={`${at}subscribed`} value={summary.subscribed} />
        <Figure label={`${at}waiting to confirm`} value={summary.pending} />
        <Figure label={`${at}unsubscribed`} value={summary.unsubscribed} />
      </Group>

      {summary.truncated && (
        <Text size="sm" mt="md">
          Counting stops at 20,000 records, and this filter reaches it, so every number above is a
          floor rather than a total: there are at least this many, and there may be a great many
          more. Narrow it down by brand or by date for an exact count, or take the CSV, which is not
          capped in the same way.
        </Text>
      )}
    </Card>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <Text ff="heading" fw={600} fz={28} lh={1} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value.toLocaleString('en-GB')}
      </Text>
      <Text size="sm" c="dimmed" mt={4}>
        {label}
      </Text>
    </div>
  );
}

/**
 * The CSV.
 *
 * The link is handed over rather than the file, it works once and it stops working after fifteen
 * minutes, because what is in it is a file full of email addresses. That is worth saying on the
 * screen: somebody who saves the link expecting to use it again on Friday would otherwise find out
 * the hard way.
 */
function ExportPanel({
  onExport,
  exporting,
  download,
  problem,
  filtered,
  listName,
}: {
  onExport: () => void;
  exporting: boolean;
  download: ExportLink | null;
  problem: string | null;
  filtered: boolean;
  /** The list this export is narrowed to, named rather than left as an id nobody can read. */
  listName: string | undefined;
}) {
  return (
    <Card withBorder radius="lg" p="lg" component="section" mb="md">
      <Group justify="space-between" align="center" gap="md" wrap="wrap">
        <div>
          <Title order={2} size="h4">
            Export as CSV
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            {listName !== undefined
              ? `Exports only the "${listName}" list, with the consent wording on every row. Exporting the wrong list is the kind of mistake that is worth a second look before you press the button.`
              : filtered
                ? 'Exports exactly what the filters above are showing, with the consent wording on every row.'
                : 'Every subscriber, with the consent wording on every row.'}
          </Text>
        </div>
        <Button variant="default" onClick={onExport} disabled={exporting}>
          {exporting ? 'Preparing...' : 'Export as CSV'}
        </Button>
      </Group>

      {problem !== null && <ErrorNote message={problem} />}

      {download !== null && (
        <Card withBorder radius="md" p="md" mt="md" role="status">
          <Text size="sm" fw={500}>
            {listName !== undefined
              ? `Your export of the "${listName}" list is ready.`
              : 'Your export is ready.'}
          </Text>
          <Text size="sm" mt={4}>
            <Anchor href={download.download_url} target="_blank" rel="noreferrer">
              Download the CSV
            </Anchor>
          </Text>
          <Text size="sm" c="dimmed" mt={4}>
            The link works once and expires at {whenLabel(download.expires_at)}. Nothing is stored
            in a bucket and nothing is emailed, so if you need it again, ask for another one.
          </Text>
        </Card>
      )}
    </Card>
  );
}

function SubscriptionStatusBadge({ status }: { status: SubscriptionStatus }) {
  const colour =
    status === 'subscribed' ? 'violet' : status === 'pending' ? 'coral' : ('gray' as const);
  return (
    <Badge size="sm" radius="sm" variant="light" color={colour} tt="none">
      {subscriptionStatusLabel(status)}
    </Badge>
  );
}

function EmptyState({ filtered, onClear }: { filtered: boolean; onClear: () => void }) {
  if (filtered) {
    return (
      <Card withBorder radius="lg" p="xl" mt="lg">
        <Title order={2} size="h3">
          Nothing matches those filters
        </Title>
        <Text mt="xs">
          No signup on this publisher fits what you have narrowed it to. Widen the dates, or clear
          the filters and start again.
        </Text>
        <Button variant="default" mt="md" onClick={onClear}>
          Clear filters
        </Button>
      </Card>
    );
  }

  return (
    <Card withBorder radius="lg" p="xl" mt="lg">
      <Stack gap="xs">
        <Title order={2} size="h3">
          Nobody has signed up yet
        </Title>
        <Text>
          A subscriber lands here the moment a reader gives their address at the end of an
          interaction, along with the exact wording they agreed to. Nothing is counted before that.
        </Text>
        <Text c="dimmed" size="sm">
          Waiting to confirm means the address is on the list but the confirmation email has not
          been opened. Which of the two a signup starts as is the brand's opt in mode, on the
          brand's own settings.
        </Text>
      </Stack>
    </Card>
  );
}
